import { useEffect, useState } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  applyFleetOnboardingRouteContext,
  createInitialFleetImportBridgeDraft,
  getFleetReviewReadiness,
  FLEET_ONBOARDING_MODE_LABELS,
  type FleetOnboardingSearch,
  resolveFleetOnboardingMode,
  resolveFleetOnboardingVertical,
  FLEET_VERTICAL_ASSET_LABELS,
  FLEET_VERTICAL_LABELS,
  summarizeFleetOnboardingDraft,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import { FleetOnboardingStepper } from "@/features/operator/fleet/fleet-onboarding-stepper"
import {
  OperatorMetricCard,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import { apiClient, isApiError } from "@/lib/api/client"

interface FleetOnboardingReviewPageProps {
  onContinueToKits: (context: {
    mode: "import" | "manual"
    organizationId: string
    runId: string | null
    vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  }) => void
  onReturnToMode: () => void
  search: FleetOnboardingSearch
}

export function FleetOnboardingReviewPage({
  onContinueToKits,
  onReturnToMode,
  search,
}: FleetOnboardingReviewPageProps) {
  const { draft, setDraft } = useFleetOnboardingDraft()
  const [isHydratingImportRun, setIsHydratingImportRun] = useState(false)
  const [importHydrationError, setImportHydrationError] = useState<string | null>(null)

  useEffect(() => {
    setDraft((previous) =>
      applyFleetOnboardingRouteContext(previous, {
        mode: search.mode,
        organizationId: search.organizationId,
        runId: search.runId,
        vertical: search.vertical,
      })
    )
  }, [search.mode, search.organizationId, search.runId, search.vertical, setDraft])

  const selectedMode = resolveFleetOnboardingMode(search.mode, draft, search.step)
  const selectedVertical = resolveFleetOnboardingVertical(search.vertical, draft)
  const summary = summarizeFleetOnboardingDraft(draft)
  const readiness = getFleetReviewReadiness(draft, selectedMode, selectedVertical)
  const assetLabel = FLEET_VERTICAL_ASSET_LABELS[selectedVertical]
  const isReadyForKits = readiness.blockingIssues.length === 0
  const [persistError, setPersistError] = useState<string | null>(null)
  const [isPersisting, setIsPersisting] = useState(false)

  useEffect(() => {
    if (selectedMode !== "import") {
      setImportHydrationError(null)
      setIsHydratingImportRun(false)
      return
    }

    const organizationId =
      search.organizationId
      ?? draft.activeOrganizationId
      ?? draft.masterPersistence?.organizationId
      ?? null
    if (!organizationId) {
      return
    }

    let isActive = true
    setIsHydratingImportRun(true)
    setImportHydrationError(null)

    void apiClient
      .resolveFleetOnboardingRun({
        onboardingMode: "import",
        organizationId,
        runId: search.runId ?? draft.activeRunId ?? draft.masterPersistence?.runId ?? null,
        step: "review",
        vertical: selectedVertical,
      })
      .then((result) => {
        if (!isActive) {
          return
        }

        const latestUpload = result.run.latestUpload
        if (!latestUpload) {
          return
        }

        setDraft((previous) => ({
          ...previous,
          activeOrganizationId: result.run.organizationId,
          activeRunId: result.run.id,
          importBridge: {
            ...(previous.importBridge ?? createInitialFleetImportBridgeDraft()),
            dryRunCreated: latestUpload.acceptedRows,
            dryRunFailed: latestUpload.failedRows,
            dryRunRows: latestUpload.rowResults.map((row) => ({
              error: row.errors?.length ? row.errors.join(" ") : null,
              plateNumber: row.assetNumber || null,
              rowIndex: row.rowIndex,
              status: row.status,
              tag: null,
            })),
            dryRunTotal: latestUpload.totalRows,
            assetCount: latestUpload.validRowsCount,
            fileName: latestUpload.fileName || previous.importBridge?.fileName || "",
            lastDryRunAt: latestUpload.uploadedAt || previous.importBridge?.lastDryRunAt || null,
          },
        }))
      })
      .catch((error) => {
        if (!isActive) {
          return
        }

        setImportHydrationError(
          isApiError(error)
            ? error.message
            : error instanceof Error
              ? error.message
              : "Failed to hydrate saved upload data for review."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsHydratingImportRun(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [
    draft.activeOrganizationId,
    draft.activeRunId,
    draft.masterPersistence?.organizationId,
    draft.masterPersistence?.runId,
    search.organizationId,
    search.runId,
    selectedMode,
    selectedVertical,
    setDraft,
  ])

  async function persistAndContinue() {
    if (!isReadyForKits || isPersisting) {
      return
    }

    setIsPersisting(true)
    setPersistError(null)

    try {
      const resolvedOrganizationId =
        search.organizationId
        ?? draft.activeOrganizationId
        ?? draft.masterPersistence?.organizationId
        ?? null
      const resolvedRunId =
        search.runId
        ?? draft.activeRunId
        ?? draft.masterPersistence?.runId
        ?? null

      const persisted = await apiClient.persistFleetOnboardingMasters({
        importBridge: draft.importBridge
          ? {
              dryRunRows: draft.importBridge.dryRunRows,
              fileName: draft.importBridge.fileName,
              lastDryRunAt: draft.importBridge.lastDryRunAt,
              sourceRows: draft.importBridge.sourceRows,
            }
          : null,
        manualAssets: draft.manualAssets,
        onboardingMode: selectedMode,
        operatingRegions: draft.operatingRegions,
        organization: {
          city: draft.primaryCity,
          country: draft.primaryCountry,
          fleetAdminEmail: draft.adminEmail,
          fleetAdminName: draft.adminName,
          fleetAdminPhone: draft.adminPhone,
          fleetEnabled: true,
          fleetSize:
            selectedMode === "manual"
              ? summary.manualReadyAssets
              : draft.importBridge?.dryRunTotal || draft.importBridge?.assetCount || 0,
          legalName: draft.legalName,
          name: draft.organizationName || "Fleet Organization",
          region: draft.operatingRegions,
          type: "fleet_partner",
        },
        organizationId: resolvedOrganizationId,
        runId: resolvedRunId,
        units: draft.units,
        vertical: selectedVertical,
      })

      setDraft((previous) => ({
        ...previous,
        kitIssuance: null,
        masterPersistence: {
          assetCount: persisted.assetCount,
          assignmentCount: persisted.assignmentCount,
          lastPersistedAt: persisted.persistedAt,
          onboardingMode: persisted.onboardingMode,
          organizationId: persisted.organizationId,
          peopleCount: persisted.peopleCount,
          runId: persisted.runId || resolvedRunId,
          unitCount: persisted.unitCount,
          vertical: persisted.vertical,
        },
      }))

      onContinueToKits({
        mode: persisted.onboardingMode,
        organizationId: persisted.organizationId,
        runId: persisted.runId || resolvedRunId,
        vertical: persisted.vertical,
      })
    } catch (error) {
      setPersistError(
        isApiError(error)
          ? error.message
          : error instanceof Error
            ? error.message
            : "Failed to persist fleet onboarding masters."
      )
    } finally {
      setIsPersisting(false)
    }
  }

  return (
    <FleetB2BPageShell
      activeSection="onboarding"
      title="Onboarding readiness review"
      description="Review organization, operating-unit, asset, and import readiness before Fleet moves into system-activated kit issuance."
      highlights={[
        "Readiness cards across organization profile, operating units, assets, people, and import posture.",
        "Clear blocking issues versus warnings before the operator can enter kits and issuance.",
        "A single cutover decision from onboarding into enterprise fleet kit generation.",
      ]}
    >
      <div className="grid gap-5">
        <OperatorPanel
          title="Onboarding progress"
          description="Phase 5 turns review into a real control gate before enterprise kit issuance begins."
        >
          <FleetOnboardingStepper activeStep="review" />
        </OperatorPanel>

        <OperatorStatusMessage>
          Review mode: <strong>{FLEET_ONBOARDING_MODE_LABELS[selectedMode]}</strong>. Vertical:
          <strong> {FLEET_VERTICAL_LABELS[selectedVertical]}</strong>.
        </OperatorStatusMessage>

        {draft.masterPersistence?.lastPersistedAt ? (
          <OperatorStatusMessage>
            Persisted fleet master data is available for this draft. Latest persistence:
            <strong> {new Date(draft.masterPersistence.lastPersistedAt).toLocaleString()}</strong>.
          </OperatorStatusMessage>
        ) : null}

        {persistError ? (
          <OperatorStatusMessage tone="danger">{persistError}</OperatorStatusMessage>
        ) : null}

        {selectedMode === "import" && isHydratingImportRun ? (
          <OperatorStatusMessage>
            Loading saved upload readiness from the onboarding run.
          </OperatorStatusMessage>
        ) : null}

        {selectedMode === "import" && importHydrationError ? (
          <OperatorStatusMessage tone="danger">{importHydrationError}</OperatorStatusMessage>
        ) : null}

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          <OperatorPanel
            title="Readiness scorecard"
            description="This is the current onboarding state held in the Fleet draft. Blocks must be cleared before kit issuance should start."
          >
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <OperatorMetricCard
                label="Organization fields"
                value={`${summary.completedOrganizationFields}/6`}
                detail="Core organization fields required before B2B issuance."
              />
              <OperatorMetricCard
                label="Complete units"
                value={`${summary.completeUnits}/${summary.totalUnits}`}
                detail="Branches, depots, hubs, or route bases ready for use."
              />
              <OperatorMetricCard
                label={`${assetLabel} rows`}
                value={summary.totalManualAssets}
                detail="Manual onboarding asset rows currently stored in draft state."
              />
              <OperatorMetricCard
                label="Ready assets"
                value={summary.manualReadyAssets}
                detail="Manual rows with required asset number and operating-unit mapping."
              />
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm text-muted-foreground">Saved upload</div>
                <div className="mt-2 text-2xl font-semibold">
                  {summary.importDryRunReady ? "Ready" : "Not saved"}
                </div>
                <div className="mt-2 text-sm leading-6 text-muted-foreground">
                  {draft.importBridge?.fileName
                    ? `${draft.importBridge.fileName} • ${draft.importBridge.dryRunTotal || draft.importBridge.assetCount} rows`
                    : "No bridge file captured in the onboarding draft yet."}
                </div>
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                <div className="text-sm text-muted-foreground">Issuance gate</div>
                <div className="mt-2 text-2xl font-semibold">
                  {isReadyForKits ? "Go to kits" : "Blocked"}
                </div>
                <div className="mt-2 text-sm leading-6 text-muted-foreground">
                  {isReadyForKits
                    ? "Onboarding has enough structure to move into system-activated kit issuance."
                    : `${readiness.blockingIssues.length} blocking issue(s) must be resolved first.`}
                </div>
              </div>
            </div>
          </OperatorPanel>

          <OperatorPanel
            title="Mode-specific review"
            description="The selected onboarding mode decides which dataset is authoritative for this review step."
          >
            {selectedMode === "manual" ? (
              <div className="grid gap-3 text-sm leading-6 text-muted-foreground">
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  Manual onboarding uses the structured roster held in session storage. Every{" "}
                  {assetLabel.toLowerCase()} row must have the required asset number before the
                  review step can hand off to kits.
                </div>
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  Current organization: <strong>{draft.organizationName.trim() || "Not captured yet"}</strong>
                  <br />
                  Preferred driver language: <strong>{draft.preferredDriverLanguage}</strong>
                  <br />
                  Operating regions: <strong>{draft.operatingRegions.trim() || "Not captured yet"}</strong>
                </div>
              </div>
            ) : (
              <div className="grid gap-3 text-sm leading-6 text-muted-foreground">
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  Import onboarding uses rows saved to the onboarding run after upload validation.
                  Review becomes issuance-ready only when that saved upload is clean and contains
                  eligible vehicles.
                </div>
                <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                  Latest file: <strong>{draft.importBridge?.fileName || "None"}</strong>
                  <br />
                  Assets inferred: <strong>{draft.importBridge?.assetCount || 0}</strong>
                  <br />
                  Failed upload rows: <strong>{draft.importBridge?.dryRunFailed || 0}</strong>
                </div>
              </div>
            )}
          </OperatorPanel>
        </section>

        <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          <OperatorPanel
            title="Blocking issues"
            description="These issues must be cleared before the operator should move into kit issuance."
          >
            {readiness.blockingIssues.length === 0 ? (
              <OperatorStatusMessage>
                No blocking issues are currently open. This onboarding draft is ready to move into Fleet kits.
              </OperatorStatusMessage>
            ) : (
              <div className="grid gap-3">
                {readiness.blockingIssues.map((issue) => (
                  <OperatorStatusMessage key={issue} tone="danger">
                    {issue}
                  </OperatorStatusMessage>
                ))}
              </div>
            )}
          </OperatorPanel>

          <OperatorPanel
            title="Warnings and watchouts"
            description="Warnings do not block the route handoff, but they call out gaps that will weaken operational quality later."
          >
            {readiness.warnings.length === 0 ? (
              <OperatorStatusMessage>
                No non-blocking warnings are currently open for this onboarding draft.
              </OperatorStatusMessage>
            ) : (
              <div className="grid gap-3">
                {readiness.warnings.map((warning) => (
                  <OperatorStatusMessage key={warning} tone="warning">
                    {warning}
                  </OperatorStatusMessage>
                ))}
              </div>
            )}
          </OperatorPanel>
        </section>

        <OperatorPanel
          title="Next action"
          description="Use review as the final Fleet onboarding gate before system-issued, system-activated kits."
        >
          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onReturnToMode}
              className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
            >
              Return to {FLEET_ONBOARDING_MODE_LABELS[selectedMode]}
            </button>
            <button
              type="button"
              onClick={() => {
                void persistAndContinue()
              }}
              disabled={!isReadyForKits || isPersisting}
              className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isPersisting ? "Persisting fleet masters..." : "Continue to kits and issuance"}
            </button>
          </div>
        </OperatorPanel>
      </div>
    </FleetB2BPageShell>
  )
}
